"use client";
import { useState, useEffect, useCallback, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { GameType } from "@/lib/types";
import { getPersonalizedRecommendations, getTrendRecommendations } from "@/lib/recommendations";
import { clearRecommendationCacheAction } from '@/lib/recommendationsActions';
import { toast } from 'sonner';
import "./loading-bar.css";

interface RecommendedGamesProps {
  limit?: number;
  initialGames: GameType[];
  currentUserId?: string | null;
  showTitle?: boolean;
}

export default function RecommendedGames({
  limit = 12,
  initialGames,
  currentUserId = null,
  showTitle = true,
}: RecommendedGamesProps) {
  const [games, setGames] = useState<GameType[]>(initialGames);
  const [loading, setLoading] = useState(false);
  const userIdRef = useRef(currentUserId);
  const [tab, setTab] = useState<'popular' | 'latest' | 'oldest'>('popular');

  useEffect(() => {
    userIdRef.current = currentUserId;
  }, [currentUserId]);

  console.log(`RecommendedGames: Rendering with initial ${initialGames.length} games, currentUserId: ${currentUserId}, limit: ${limit}, showTitle: ${showTitle}`);

  const fetchGames = useCallback(async () => {
    const userId = userIdRef.current;
    console.log(`RecommendedGames: Fetching games - userId: ${userId}, limit: ${limit}`);
    setLoading(true);
    try {
      let recommendedGames;
      if (userId && userId !== 'Anon12') {
        console.log(`RecommendedGames: Calling getPersonalizedRecommendations for user ${userId}`);
        recommendedGames = await getPersonalizedRecommendations(userId, limit, 0);
      } else {
        console.log(`RecommendedGames: Calling getTrendRecommendations (userId: ${userId})`);
        recommendedGames = await getTrendRecommendations(limit, 0);
      }
      console.log(`RecommendedGames: Fetched ${recommendedGames?.length ?? 0} games.`);
      setGames(recommendedGames || []);
    } catch (error) {
      console.error("Error fetching recommended games:", error);
      toast.error("추천 게임을 불러오는데 실패했습니다.");
      setGames([]);
    } finally {
      setLoading(false);
      console.log(`RecommendedGames: Fetch complete - loading: ${false}`);
    }
  }, [limit]);

  const getSortedGames = () => {
    if (!games) return [];
    if (tab === 'popular') {
      return [...games].sort((a, b) => (b.plays ?? 0) - (a.plays ?? 0) || (b.views ?? 0) - (a.views ?? 0));
    } else if (tab === 'latest') {
      return [...games].sort((a, b) => {
        const aDate = a.dateAdded ? new Date(a.dateAdded).getTime() : 0;
        const bDate = b.dateAdded ? new Date(b.dateAdded).getTime() : 0;
        return bDate - aDate;
      });
    } else if (tab === 'oldest') {
      return [...games].sort((a, b) => {
        const aDate = a.dateAdded ? new Date(a.dateAdded).getTime() : 0;
        const bDate = b.dateAdded ? new Date(b.dateAdded).getTime() : 0;
        return aDate - bDate;
      });
    }
    return games;
  };
  const sortedGames = getSortedGames();

  const getCardClass = (index: number) => {
    const rotations = ["rotate-0", "rotate-1", "-rotate-1"];
    return rotations[index % rotations.length];
  };

  if (loading && games.length === 0) {
    return (
      <div className="loading-skeleton">
        {showTitle && (
          <div className="skeleton-title mb-8">
            <div className="skeleton-text bg-gray-200 dark:bg-gray-700"></div>
            <div className="skeleton-underline bg-orange-500 dark:bg-orange-400"></div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {Array.from({ length: limit }).map((_, idx) => (
            <div key={idx} className="skeleton-card bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <div className="skeleton-image bg-gray-200 dark:bg-gray-700"></div>
              <div className="skeleton-content">
                <div className="skeleton-title-line bg-gray-300 dark:bg-gray-600"></div>
                <div className="skeleton-desc-line bg-gray-300 dark:bg-gray-600"></div>
                <div className="skeleton-desc-line shorter bg-gray-300 dark:bg-gray-600"></div>
                <div className="skeleton-tags">
                  <div className="skeleton-tag bg-orange-100 dark:bg-orange-900"></div>
                  <div className="skeleton-tag bg-orange-100 dark:bg-orange-900"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="recommended-games">
      {showTitle && (
        <div className="title-container mb-6">
          <h2 className="title-text text-orange-600 dark:text-orange-400">{currentUserId && currentUserId !== 'Anon12' ? "맞춤 게임 추천" : "인기 게임"}</h2>
          <div className="title-underline bg-gradient-to-r from-orange-500 to-orange-600 dark:from-orange-400 dark:to-orange-500"></div>
        </div>
      )}
      
      <div className="tab-container flex justify-center gap-2 mb-6">
        <button
          className={`tab-btn px-4 py-2 rounded-full font-semibold border transition-colors ${tab === 'popular' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white dark:bg-gray-800 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-700'}`}
          onClick={() => setTab('popular')}
        >
          인기순
        </button>
        <button
          className={`tab-btn px-4 py-2 rounded-full font-semibold border transition-colors ${tab === 'latest' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white dark:bg-gray-800 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-700'}`}
          onClick={() => setTab('latest')}
        >
          최신순
        </button>
        <button
          className={`tab-btn px-4 py-2 rounded-full font-semibold border transition-colors ${tab === 'oldest' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white dark:bg-gray-800 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-700'}`}
          onClick={() => setTab('oldest')}
        >
          오래된순
        </button>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
        {sortedGames.map((game, index) => (
          <Link
            href={`/games/${game.slug}`}
            key={game.id}
            className="block transform transition-transform duration-300 hover:scale-105 group"
          >
            <Card className={`overflow-hidden h-full game-card bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 ${getCardClass(index)}`}>
              <div className="relative h-48 w-full overflow-hidden">
                <Image
                  src={game.thumbnail || "/placeholder.png"}
                  alt={game.title}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/placeholder.png";
                  }}
                  fill
                  className="object-cover transition-transform duration-500 group-hover:scale-110"
                />
                {game.isNew && (
                  <div className="absolute top-2 right-2">
                    <Badge className="new-badge bg-gradient-to-r from-orange-500 to-orange-600 text-white border-none">New</Badge>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/50 to-transparent text-white p-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs flex items-center stat-bubble views-stat">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      {game.views?.toLocaleString() || 0}
                    </span>
                    <span className="text-xs flex items-center stat-bubble plays-stat">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {game.plays?.toLocaleString() || 0}
                    </span>
                    <span className="text-xs flex items-center stat-bubble likes-stat">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      {game.likes?.toLocaleString() || 0}
                    </span>
                  </div>
                </div>
              </div>
              <div className="p-4 game-info">
                <h3 className="game-title text-orange-700 dark:text-orange-400 group-hover:text-orange-600 dark:group-hover:text-orange-300">{game.title}</h3>
                <p className="game-description text-gray-600 dark:text-gray-400">{game.description}</p>
                <div className="mt-3 flex flex-wrap gap-1">
                  {game.categories?.slice(0, 3).map((category, idx) => (
                    <Badge
                      key={idx}
                      variant="outline"
                      className="category-badge border-orange-300 dark:border-orange-700 text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/30 group-hover:bg-orange-100 dark:group-hover:bg-orange-800/50"
                    >
                      {category}
                    </Badge>
                  ))}
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>
      
      <style jsx global>{`
        .game-card {
          position: relative;
          border-radius: 16px;
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          overflow: hidden;
        }

        .game-card:hover {
          transform: translateY(-5px);
        }

        .new-badge {
          font-weight: bold;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.7rem;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }

        .category-badge {
          transition: all 0.3s ease;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: capitalize;
        }

        .category-badge:hover {
          transform: translateY(-2px);
        }

        .game-title {
          font-size: 1.25rem;
          font-weight: 800;
          line-height: 1.3;
          margin-bottom: 0.5rem;
          letter-spacing: -0.02em;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          transition: color 0.3s ease;
        }

        .game-description {
          font-size: 0.875rem;
          line-height: 1.4;
          margin-bottom: 0.75rem;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        .game-info {
          position: relative;
          z-index: 2;
          transition: transform 0.3s ease;
        }

        .stat-bubble {
          background: rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(4px);
          padding: 2px 6px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          font-weight: 600;
          transition: all 0.3s ease;
        }
        
        .stat-bubble:hover {
          transform: scale(1.1);
          background: rgba(0, 0, 0, 0.5);
        }

        .title-container {
          text-align: center;
          position: relative;
        }
        
        .title-text {
          font-size: 1.75rem;
          font-weight: 800;
          display: inline-block;
          margin-bottom: 0.5rem;
        }
        
        .title-underline {
          height: 4px;
          width: 80px;
          margin: 0 auto;
          border-radius: 4px;
        }

        .loading-skeleton {
          width: 100%;
        }

        .skeleton-card {
          height: 320px;
          border-radius: 16px;
          overflow: hidden;
          position: relative;
        }

        .skeleton-image {
          height: 180px;
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }

        .skeleton-content {
          padding: 16px;
        }

        .skeleton-title-line {
          height: 24px;
          width: 80%;
          margin-bottom: 12px;
          border-radius: 4px;
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }

        .skeleton-desc-line {
          height: 12px;
          width: 100%;
          margin-bottom: 8px;
          border-radius: 2px;
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }

        .skeleton-desc-line.shorter {
          width: 60%;
        }

        .skeleton-tags {
          display: flex;
          margin-top: 12px;
          gap: 8px;
        }

        .skeleton-tag {
          height: 20px;
          width: 60px;
          border-radius: 12px;
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }

        .skeleton-title {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .skeleton-text {
          height: 40px;
          width: 280px;
          border-radius: 4px;
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          margin-bottom: 8px;
        }

        .skeleton-underline {
          height: 4px;
          width: 80px;
          border-radius: 4px;
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        @media (max-width: 768px) {
          .skeleton-card {
            height: 280px;
          }

          .skeleton-image {
            height: 150px;
          }

          .title-text {
            font-size: 1.5rem;
          }

          .game-title {
            font-size: 1.1rem;
          }

          .game-description {
            font-size: 0.8rem;
          }
        }

        .tab-btn {
          min-width: 90px;
        }
        .tab-btn:not(.bg-orange-500):hover {
          background: #fff7ed;
          border-color: #fdba74;
        }
      `}</style>
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (prefers-reduced-motion: no-preference) {
          .grid > .group {
            animation: fadeInUp 0.6s ease-out forwards;
            opacity: 0;
          }

          .grid > .group:nth-child(1) { animation-delay: 0.1s; }
          .grid > .group:nth-child(2) { animation-delay: 0.15s; }
          .grid > .group:nth-child(3) { animation-delay: 0.2s; }
          .grid > .group:nth-child(4) { animation-delay: 0.25s; }
          .grid > .group:nth-child(5) { animation-delay: 0.3s; }
          .grid > .group:nth-child(6) { animation-delay: 0.35s; }
          .grid > .group:nth-child(7) { animation-delay: 0.4s; }
          .grid > .group:nth-child(8) { animation-delay: 0.45s; }
          .grid > .group:nth-child(9) { animation-delay: 0.5s; }
          .grid > .group:nth-child(10) { animation-delay: 0.55s; }
          .grid > .group:nth-child(11) { animation-delay: 0.6s; }
          .grid > .group:nth-child(12) { animation-delay: 0.65s; }
        }

        .recommended-games {
          margin-bottom: 3rem;
        }
      `}</style>
    </div>
  );
} 